/**
 * Quick Connections Tab - Apple-style minimal connection view
 * 
 * Shows connected doctypes with counts and quick-create buttons in a clean grid layout.
 * Reuses existing useDocConnections hook and DocConnectionGroup types.
 */
import React from 'react';
import { useDocConnections } from '@/features/common/api/useDocConnections';
import { useRelationshipGraph } from '@/features/common/api/useRelationshipGraph';

import { isRelationshipGraphEnabled } from "@/features/common/flags/relationshipGraph";
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Banknote,
  Barcode,
  Boxes,
  ClipboardList,
  FileText,
  FilePen,
  GitBranch,
  Layers,
  Package,
  Percent,
  Plus,
  Receipt,
  ShoppingCart,
  Store,
  Shuffle,
  Truck,
  Wrench,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { buildNewRoute, buildListRoute, buildListRouteWithFilters } from '@/lib/config/docRoutes';
import type { DocConnectionGroup, DocConnectionsResponse, RelationshipGraphGroup, FrappeFilter } from '@/lib/erp/docs';
import { useLanguage } from '@/i18n/useLanguage';
import { getUITranslation, getDoctypeLabelTranslation } from '@/lib/views/labels/translations';
import { commonTranslations } from '@/lib/views/labels/translations/common';

interface QuickConnectionsTabProps {
  doctype: string;
  name: string;
}

interface ConnectionGroup {
  group_id: string;
  group_label: string;
  items: Array<{
    target_doctype: string;
    target_label: string;
    count: number;
    edge_id?: string;  // For graph-driven groups, use as key
    has_more?: boolean;  // For graph-driven groups, indicates if more docs exist
    filters?: Array<[string, string, any] | [string, string, string, any]>;  // Backend-driven filter support
    resolution?: "query" | "structural_only" | "error";  // Resolution status
  }>;
}

/**
 * Translate connection group label
 */
function translateGroupLabel(
  groupId: string,
  groupLabel: string,
  language: 'en' | 'th'
): string {
  if (language === 'en') return groupLabel;
  
  // Map common group IDs to translation keys (without common: prefix)
  const groupTranslationMap: Record<string, string> = {
    'pricing': 'connection_group_pricing',
    'sales': 'connection_group_sales',
    'purchase': 'connection_group_purchase',
    'stock': 'connection_group_stock',
    'manufacturing': 'connection_group_manufacturing',
    'other': 'connection_group_other',
    'groups': 'connection_group_connections',
  };
  
  const translationKey = groupTranslationMap[groupId.toLowerCase()];
  if (translationKey) {
    const fullKey = `common:${translationKey}`;
    const translation = commonTranslations[fullKey];
    if (translation?.[language]) {
      return translation[language];
    }
  }
  
  // Fallback: try to translate common labels directly by matching label text
  const labelLower = groupLabel.toLowerCase().trim();
  const labelKeyMap: Record<string, string> = {
    'pricing': 'connection_group_pricing',
    'sales': 'connection_group_sales',
    'purchase': 'connection_group_purchase',
    'stock': 'connection_group_stock',
    'manufacturing': 'connection_group_manufacturing',
    'other': 'connection_group_other',
    'groups': 'connection_group_connections',
    'connections': 'connection_group_connections',
  };
  
  const matchedKey = labelKeyMap[labelLower];
  if (matchedKey) {
    const fullKey = `common:${matchedKey}`;
    const translation = commonTranslations[fullKey];
    if (translation?.[language]) {
      return translation[language];
    }
  }
  
  return groupLabel;
}

/**
 * Group connections by group_id for display
 * Uses the same grouping logic as System Connections tab for consistency
 */
function groupConnections(
  groups: DocConnectionGroup[],
  language: 'en' | 'th'
): ConnectionGroup[] {
  const groupMap = new Map<string, ConnectionGroup>();
  
  for (const item of groups) {
    const groupId = item.group_id || 'other';
    const rawGroupLabel = item.group_label || 'Other';
    const groupLabel = translateGroupLabel(groupId, rawGroupLabel, language);
    
    if (!groupMap.has(groupId)) {
      groupMap.set(groupId, {
        group_id: groupId,
        group_label: groupLabel,
        items: [],
      });
    }
    
    groupMap.get(groupId)!.items.push({
      target_doctype: item.target_doctype,
      target_label: item.target_label,
      count: item.count,
      edge_id: (item as RelationshipGraphGroup).edge_id,  // Preserve edge_id if available
      has_more: (item as RelationshipGraphGroup).has_more,  // Preserve has_more if available
      // NEW: Preserve filters and resolution for backend-driven navigation
      filters: (item as RelationshipGraphGroup).filters,
      resolution: (item as RelationshipGraphGroup).resolution,
    });
  }
  
  // Sort: "other" group last (same as System Connections tab)
  return Array.from(groupMap.values()).sort((a, b) => {
    if (a.group_id === 'other') return 1;
    if (b.group_id === 'other') return -1;
    return 0;
  });
}

function getDoctypeIcon(doctype: string) {
  // Keep this mapping minimal and extend as needed.
  // Fallback is a generic document icon.
  switch (doctype) {
    case 'Item Price':
      return { Icon: Banknote, bg: 'bg-gradient-to-br from-emerald-500 to-emerald-600' };
    case 'Pricing Rule':
      return { Icon: Percent, bg: 'bg-gradient-to-br from-indigo-500 to-indigo-600' };
    case 'Quotation':
      return { Icon: FilePen, bg: 'bg-gradient-to-br from-blue-500 to-blue-600' };
    case 'Supplier Quotation':
      return { Icon: FilePen, bg: 'bg-gradient-to-br from-green-500 to-green-600' };
    case 'Sales Order':
      return { Icon: ShoppingCart, bg: 'bg-gradient-to-br from-purple-500 to-purple-600' };
    case 'Purchase Order':
      return { Icon: Store, bg: 'bg-gradient-to-br from-amber-500 to-amber-600' };
    case 'Purchase Receipt':
      return { Icon: Package, bg: 'bg-gradient-to-br from-emerald-500 to-emerald-600' };
    case 'Delivery Note':
      return { Icon: Truck, bg: 'bg-gradient-to-br from-orange-500 to-orange-600' };
    case 'Stock Entry':
      return { Icon: Package, bg: 'bg-gradient-to-br from-teal-500 to-teal-600' };
    case 'Stock Reconciliation':
      return { Icon: ClipboardList, bg: 'bg-gradient-to-br from-amber-500 to-amber-600' };
    case 'Material Request':
      return { Icon: ClipboardList, bg: 'bg-gradient-to-br from-violet-500 to-violet-600' };
    case 'Sales Invoice':
    case 'Purchase Invoice':
      return { Icon: Receipt, bg: 'bg-gradient-to-br from-indigo-500 to-indigo-600' };
    case 'BOM':
      return { Icon: Layers, bg: 'bg-gradient-to-br from-slate-500 to-slate-600' };
    case 'Work Order':
      return { Icon: Wrench, bg: 'bg-gradient-to-br from-stone-500 to-stone-600' };
    case 'Product Bundle':
      return { Icon: Boxes, bg: 'bg-gradient-to-br from-cyan-500 to-cyan-600' };
    case 'Item Alternative':
      return { Icon: Shuffle, bg: 'bg-gradient-to-br from-pink-500 to-pink-600' };
    // Traceability-related doctypes
    case 'Serial No':
    case 'Batch':
      return { Icon: Barcode, bg: 'bg-gradient-to-br from-lime-500 to-lime-600' };
    case 'Stock Ledger Entry':
      return { Icon: GitBranch, bg: 'bg-gradient-to-br from-zinc-500 to-zinc-600' };
    default:
      return { Icon: FileText, bg: 'bg-gradient-to-br from-gray-500 to-gray-600' };
  }
}

function getDoctypeDescription(
  doctype: string,
  language: 'en' | 'th'
): string {
  const descriptions: Record<string, { en: string; th: string }> = {
    'Sales Order': {
      en: 'Customer order to sell items',
      th: 'คำสั่งขายให้ลูกค้า',
    },
    'Delivery Note': {
      en: 'Delivery document for shipped items',
      th: 'เอกสารส่งของ/จัดส่งสินค้า',
    },
    'Sales Invoice': {
      en: 'Invoice billed to a customer',
      th: 'ใบแจ้งหนี้/ใบกำกับให้ลูกค้า',
    },
    'Purchase Order': {
      en: 'Order placed with a supplier',
      th: 'ใบสั่งซื้อถึงผู้จำหน่าย',
    },
    'Purchase Invoice': {
      en: 'Supplier invoice / bill',
      th: 'ใบแจ้งหนี้จากผู้จำหน่าย',
    },
    'Purchase Receipt': {
      en: 'Goods received from supplier',
      th: 'รับสินค้าจากผู้จำหน่าย',
    },
    'Stock Entry': {
      en: 'Stock movement transaction',
      th: 'รายการเคลื่อนไหวสต็อก',
    },
    'Stock Reconciliation': {
      en: 'Adjust stock counts to match reality',
      th: 'ปรับยอดสต็อกตามการตรวจนับ',
    },
    'Material Request': {
      en: 'Request to procure or move stock',
      th: 'คำขอจัดซื้อ/ขอเบิก-โอนสต็อก',
    },
    'Quotation': {
      en: 'Sales quote / offer to customer',
      th: 'ใบเสนอราคาให้ลูกค้า',
    },
    'Supplier Quotation': {
      en: 'Supplier quote / offer from supplier',
      th: 'ใบเสนอราคาจากผู้จำหน่าย',
    },
    'Item Price': {
      en: 'Price list entries for this item',
      th: 'ราคาสินค้าในรายการราคา',
    },
    'Pricing Rule': {
      en: 'Automatic pricing / discount rules',
      th: 'กฎราคา/ส่วนลดอัตโนมัติ',
    },
    'Product Bundle': {
      en: 'Bundle of multiple items sold together',
      th: 'ชุดสินค้าหลายรายการขายรวมกัน',
    },
    'Item Alternative': {
      en: 'Alternative/substitute items',
      th: 'สินค้าทดแทน/ทางเลือก',
    },
    'BOM': {
      en: 'Bill of Materials for manufacturing',
      th: 'สูตรการผลิต (BOM)',
    },
    'Work Order': {
      en: 'Manufacturing work order',
      th: 'คำสั่งผลิต',
    },
    'Serial No': {
      en: 'Track items by serial number',
      th: 'ติดตามสินค้าด้วยเลขซีเรียล',
    },
    'Batch': {
      en: 'Track items by batch/lot',
      th: 'ติดตามสินค้าด้วยล็อต/แบทช์',
    },
    'Stock Ledger Entry': {
      en: 'Stock ledger / inventory trace',
      th: 'บัญชีสต็อก/การติดตามสต็อก',
    },
  };

  const entry = descriptions[doctype];
  if (!entry) return '';
  return entry[language] || entry.en;
}

export function QuickConnectionsTab({ doctype, name }: QuickConnectionsTabProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // Evaluate feature flag inside component (reactive, testable, HMR-friendly)
  const useGraphFlag = isRelationshipGraphEnabled();
  
  // Base enable condition for graph query
  const baseGraphEnabled = useGraphFlag && !!doctype && !!name && name !== "new";
  
  // State latch: once we detect allowlist-filtered or graph error, disable graph query permanently
  // This prevents refetch loops and background churn
  const [allowlistFiltered, setAllowlistFiltered] = React.useState(false);
  const [graphErrored, setGraphErrored] = React.useState(false);
  
  // Graph query enabled only if base conditions met AND not allowlist-filtered AND not errored
  const graphEnabled = baseGraphEnabled && !allowlistFiltered && !graphErrored;
  
  // Conditional hook usage based on feature flag and allowlist state
  const { data: graphData, isLoading: graphLoading, error: graphError } = useRelationshipGraph(
    doctype,
    name,
    { enabled: graphEnabled }
  );

  // Legacy query enabled if feature flag is off OR allowlist-filtered OR graph errored
  const legacyEnabled = !useGraphFlag || allowlistFiltered || graphErrored;
  const query = useDocConnections(doctype, name, { enabled: legacyEnabled });
  // Cast to expected shape to keep TS happy even if React Query generics drift.
  const legacyData = query.data as DocConnectionsResponse | null | undefined;
  const legacyLoading = query.isLoading;
  const legacyError = query.error;
  
  // Latch allowlist-filtered state after first response
  // This ensures we only make one graph request for deny-all doctypes
  React.useEffect(() => {
    if (graphData?.enabled === false && graphData?.reason === "allowlist_filtered") {
      setAllowlistFiltered(true);
    }
  }, [graphData?.enabled, graphData?.reason]);
  
  // Latch graph error state - if graph fails, fall back to legacy permanently
  React.useEffect(() => {
    if (graphError) {
      setGraphErrored(true);
    }
  }, [graphError]);
  
  // Determine which data source to use for rendering
  // Use legacy if: feature flag off, allowlist-filtered, OR graph errored
  // This ensures we always have a working data source (legacy mirrors ERPNext)
  const shouldUseLegacy = !useGraphFlag || allowlistFiltered || graphErrored;
  
  // Use graph data if enabled and not allowlist-filtered, otherwise legacy
  const data = shouldUseLegacy ? legacyData : graphData;
  const isLoading = shouldUseLegacy ? legacyLoading : graphLoading;
  const error = shouldUseLegacy ? legacyError : graphError;
  
  // Group connections for display (same grouping as System Connections tab)
  const grouped = React.useMemo(
    () => groupConnections(data?.groups || [], language),
    [data?.groups, language]
  );
  
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, colIdx) => (
            <div key={colIdx} className="space-y-3">
              <Skeleton className="h-4 w-24" />
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 rounded-md" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (error) {
    const errorMessage = getUITranslation('connections_load_failed', language) || 
      'Failed to load connections. Please try again.';
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            {errorMessage}
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  if (grouped.length === 0) {
    const emptyMessage = getUITranslation('no_connections_available', language) || 
      'No connections available';
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      {/* Each column is one ERPNext group; items inside the column are vertical */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-20">
        {grouped.map((group) => (
          <div key={group.group_id} className="space-y-3">
            {/* Group header */}
            <div className="px-1">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {group.group_label}
              </h3>
            </div>

            {/* Vertical list of connection cards */}
            <div className="space-y-3">
              {group.items.map((item) => {
                const { Icon, bg } = getDoctypeIcon(item.target_doctype);
                // Use edge_id if available (graph-driven), fallback to target_doctype (legacy)
                const itemKey = item.edge_id || item.target_doctype;
                // Display count with "+" if has_more is true
                const countDisplay = item.has_more ? `${item.count}+` : `${item.count}`;

                return (
                  <div
                    key={itemKey}
                    className={cn(
                      // PERF: avoid backdrop-filter blur on scroll (glass/nav-glass are expensive to repaint)
                      // Keep it visually similar but with solid/semi-solid surfaces.
                      "group relative rounded-md bg-card border border-border/40 shadow-sm",
                      "min-h-[64px] px-4 py-3.5 cursor-pointer touch-manipulation",
                      "transition-colors duration-200 ease-out",
                      "hover:border-border/60 hover:shadow-md",
                      "active:translate-y-[0.5px]"
                    )}
                    style={{
                      // PERF: let browser skip rendering offscreen cards (helps long lists)
                      contentVisibility: "auto",
                      containIntrinsicSize: "72px",
                    }}
                    onClick={async () => {
                      const filters = item.filters;
                      const resolution = item.resolution ?? "query";

                      // Backend-driven navigation (preferred) - filters-first approach
                      if (resolution === "query" && filters && filters.length > 0) {
                        try {
                          const route = await buildListRouteWithFilters(
                            item.target_doctype,
                            filters as FrappeFilter[]
                          );
                          navigate(route);
                          return;
                        } catch (err) {
                          console.error("[QuickConnectionsTab] buildListRouteWithFilters failed:", err);
                          // Fallback to unfiltered list on error (buildListRoute never throws)
                          navigate(buildListRoute(item.target_doctype));
                          return;
                        }
                      }

                      // No filters or structural_only → unfiltered list (buildListRoute never throws)
                      navigate(buildListRoute(item.target_doctype));
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2.5 flex-1 min-w-0">
                        <div
                          className={cn(
                            // Match SidebarContent icon tile style
                            "w-6 h-6 rounded-sm flex items-center justify-center shrink-0 transition-all duration-200 self-start",
                            bg,
                            "shadow-sm"
                          )}
                          aria-hidden="true"
                        >
                          <Icon className="w-3 h-3 text-white" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="font-medium text-sm truncate">
                              {getDoctypeLabelTranslation(item.target_doctype, language) || item.target_label}
                            </div>
                            {item.count > 0 ? (
                              <div
                                className={cn(
                                  "inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium shrink-0",
                                  "bg-primary/10 text-primary"
                                )}
                              >
                                {countDisplay}
                              </div>
                            ) : null}
                          </div>

                        <div className="mt-0.5 text-[11px] text-muted-foreground leading-snug">
                          {getDoctypeDescription(item.target_doctype, language) ||
                            (getUITranslation('related_doctype_records', language) || 
                              `Related ${getDoctypeLabelTranslation(item.target_doctype, language) || item.target_label} records`).replace(/\{doctype\}/g, getDoctypeLabelTranslation(item.target_doctype, language) || item.target_label)}
                        </div>
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          // Touch-friendly hit target (>= 44px) while keeping a smaller visual inside
                          "h-11 w-11 p-0 rounded-full shrink-0",
                          "hover:bg-muted/40 active:bg-muted/60 active:scale-95",
                          "transition-colors duration-200 touch-manipulation",
                          "opacity-60 group-hover:opacity-100"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = buildNewRoute(item.target_doctype, {
                            link_doctype: doctype,
                            link_name: name,
                          });
                          navigate(url);
                        }}
                        title={(getUITranslation('create_new_doctype', language) || 
                          `Create new ${getDoctypeLabelTranslation(item.target_doctype, language) || item.target_label}`).replace(/\{doctype\}/g, getDoctypeLabelTranslation(item.target_doctype, language) || item.target_label)}
                      >
                        <span className="h-7 w-7 rounded-full bg-background/80 dark:bg-background/60 shadow-sm border border-border/40 flex items-center justify-center">
                          <Plus className="h-3.5 w-3.5" />
                        </span>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

